import { useState, useEffect } from "react";
import {
  Calendar,
  MapPin,
  Users,
  Clock,
  BookOpen,
  Globe,
  DollarSign,
  User,
  Building,
  Trophy,
  MessageSquare,
  Download,
  UserCheck,
  UserX,
  CheckCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarInitials } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { trainingAPI } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";

const getStatusColor = (status) => {
  switch (status) {
    case "planifiee":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
    case "en_cours":
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
    case "terminee":
      return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    case "annulee":
      return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
  }
};

const getInscriptionStatusColor = (status) => {
  switch (status) {
    case "inscrit":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
    case "confirme":
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
    case "complete":
      return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300";
    case "absent":
      return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
    case "annule":
      return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
  }
};

const formatStatusLabel = (status) => {
  switch (status) {
    case "planifiee":
      return "Planifiée";
    case "en_cours":
      return "En cours";
    case "terminee":
      return "Terminée";
    case "annulee":
      return "Annulée";
    default:
      return status;
  }
};

const formatInscriptionStatusLabel = (status) => {
  switch (status) {
    case "inscrit":
      return "Inscrit";
    case "confirme":
      return "Confirmé";
    case "complete":
      return "Terminé";
    case "absent":
      return "Absent";
    case "annule":
      return "Annulé";
    default:
      return status;
  }
};

const formatTypeLabel = (type) => {
  switch (type) {
    case "interne":
      return "Formation Interne";
    case "externe":
      return "Formation Externe";
    case "en_ligne":
      return "Formation en Ligne";
    default:
      return type;
  }
};

const getTypeIcon = (type) => {
  switch (type) {
    case "interne":
      return <BookOpen className="h-4 w-4" />;
    case "externe":
      return <MapPin className="h-4 w-4" />;
    case "en_ligne":
      return <Globe className="h-4 w-4" />;
    default:
      return <BookOpen className="h-4 w-4" />;
  }
};

export default function TrainingDetails({ formation, onClose }) {
  const [detailedFormation, setDetailedFormation] = useState(formation);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const canManageTraining = user?.role === "admin" || user?.role === "rh";

  useEffect(() => {
    if (formation?.id) {
      fetchDetailedFormation();
    }
  }, [formation?.id]);

  const fetchDetailedFormation = async () => {
    try {
      setLoading(true);
      const response = await trainingAPI.getById(formation.id);
      setDetailedFormation(response.data);
    } catch (error) {
      console.error("Error fetching detailed formation:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateInscriptionStatus = async (inscriptionId, newStatus) => {
    try {
      await trainingAPI.updateEnrollmentStatus(inscriptionId, {
        statut: newStatus,
      });

      toast({
        title: "Succès",
        description: "Statut mis à jour avec succès",
      });

      fetchDetailedFormation();
    } catch (error) {
      toast({
        title: "Erreur",
        description:
          error.response?.data?.message || "Erreur lors de la mise à jour",
        variant: "destructive",
      });
    }
  };

  if (!detailedFormation) return null;

  const inscriptions = detailedFormation.inscriptions || [];
  const completionRate =
    inscriptions.length > 0
      ? Math.round(
          (inscriptions.filter((i) => i.statut === "complete").length /
            inscriptions.length) *
            100
        )
      : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-3 mb-2">
            {getTypeIcon(detailedFormation.type_formation)}
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {detailedFormation.titre}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={getStatusColor(detailedFormation.statut)}>
              {formatStatusLabel(detailedFormation.statut)}
            </Badge>
            <Badge variant="outline">
              {formatTypeLabel(detailedFormation.type_formation)}
            </Badge>
          </div>
        </div>
      </div>

      {/* Description */}
      {detailedFormation.description && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-gray-700 dark:text-gray-300">
              {detailedFormation.description}
            </p>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
          <TabsTrigger value="participants">
            Participants ({inscriptions.length})
          </TabsTrigger>
          <TabsTrigger value="progress">Progression</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Formation Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  Informations de la formation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {detailedFormation.date_debut && (
                  <div className="flex items-center gap-3">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-500">Date de début</p>
                      <p className="font-medium">
                        {new Date(
                          detailedFormation.date_debut
                        ).toLocaleDateString("fr-FR", {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </p>
                    </div>
                  </div>
                )}

                {detailedFormation.date_fin && (
                  <div className="flex items-center gap-3">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-500">Date de fin</p>
                      <p className="font-medium">
                        {new Date(
                          detailedFormation.date_fin
                        ).toLocaleDateString("fr-FR", {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </p>
                    </div>
                  </div>
                )}

                {detailedFormation.duree && (
                  <div className="flex items-center gap-3">
                    <Clock className="h-4 w-4 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-500">Durée</p>
                      <p className="font-medium">
                        {detailedFormation.duree} heures
                      </p>
                    </div>
                  </div>
                )}

                {detailedFormation.capacite && (
                  <div className="flex items-center gap-3">
                    <Users className="h-4 w-4 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-500">Capacité</p>
                      <p className="font-medium">
                        {inscriptions.length}/{detailedFormation.capacite}{" "}
                        participants
                      </p>
                    </div>
                  </div>
                )}

                {detailedFormation.cout && (
                  <div className="flex items-center gap-3">
                    <DollarSign className="h-4 w-4 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-500">Coût</p>
                      <p className="font-medium">
                        {new Intl.NumberFormat("fr-MA", {
                          style: "currency",
                          currency: "MAD",
                        }).format(detailedFormation.cout)}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Instructor and Location */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Formateur et lieu
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {detailedFormation.formateur && (
                  <div className="flex items-center gap-3">
                    <User className="h-4 w-4 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-500">Formateur</p>
                      <p className="font-medium">
                        {detailedFormation.formateur}
                      </p>
                    </div>
                  </div>
                )}

                {detailedFormation.organisme && (
                  <div className="flex items-center gap-3">
                    <Building className="h-4 w-4 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-500">Organisme</p>
                      <p className="font-medium">
                        {detailedFormation.organisme}
                      </p>
                    </div>
                  </div>
                )}

                {detailedFormation.lieu && (
                  <div className="flex items-center gap-3">
                    <MapPin className="h-4 w-4 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-500">Lieu</p>
                      <p className="font-medium">{detailedFormation.lieu}</p>
                    </div>
                  </div>
                )}

                {detailedFormation.type_formation === "en_ligne" &&
                  !detailedFormation.lieu && (
                    <div className="flex items-center gap-3">
                      <Globe className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-500">Modalité</p>
                        <p className="font-medium">Formation en ligne</p>
                      </div>
                    </div>
                  )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="participants" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Liste des participants</CardTitle>
            </CardHeader>
            <CardContent>
              {inscriptions.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Aucun participant
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Aucune inscription pour cette formation.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {inscriptions.map((inscription) => (
                    <div
                      key={inscription.id}
                      className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarFallback>
                            <AvatarInitials
                              name={`${inscription.employe?.prenom} ${inscription.employe?.nom}`}
                            />
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {inscription.employe?.prenom}{" "}
                            {inscription.employe?.nom}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {inscription.employe?.email}
                          </p>
                          <p className="text-xs text-gray-500">
                            Inscrit le{" "}
                            {new Date(
                              inscription.date_inscription
                            ).toLocaleDateString("fr-FR")}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge
                          className={getInscriptionStatusColor(
                            inscription.statut
                          )}
                        >
                          {formatInscriptionStatusLabel(inscription.statut)}
                        </Badge>
                        {canManageTraining &&
                          inscription.statut === "inscrit" && (
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  updateInscriptionStatus(
                                    inscription.id,
                                    "confirme"
                                  )
                                }
                              >
                                <UserCheck className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  updateInscriptionStatus(
                                    inscription.id,
                                    "annule"
                                  )
                                }
                              >
                                <UserX className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        {canManageTraining &&
                          inscription.statut === "confirme" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                updateInscriptionStatus(
                                  inscription.id,
                                  "complete"
                                )
                              }
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                          )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="progress" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Taux de complétion
                </CardTitle>
                <Trophy className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{completionRate}%</div>
                <Progress value={completionRate} className="mt-2" />
                <p className="text-xs text-muted-foreground mt-2">
                  {inscriptions.filter((i) => i.statut === "complete").length}{" "}
                  sur {inscriptions.length} terminés
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Confirmés</CardTitle>
                <UserCheck className="h-4 w-4 text-muted-foreference" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {inscriptions.filter((i) => i.statut === "confirme").length}
                </div>
                <p className="text-xs text-muted-foreground">
                  Participants confirmés
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  En attente
                </CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {inscriptions.filter((i) => i.statut === "inscrit").length}
                </div>
                <p className="text-xs text-muted-foreground">
                  Inscriptions en attente
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Detailed progress for completed participants */}
          {inscriptions.some((i) => i.statut === "complete") && (
            <Card>
              <CardHeader>
                <CardTitle>Participants ayant terminé</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {inscriptions
                    .filter((i) => i.statut === "complete")
                    .map((inscription) => (
                      <div
                        key={inscription.id}
                        className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarFallback>
                              <AvatarInitials
                                name={`${inscription.employe?.prenom} ${inscription.employe?.nom}`}
                              />
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {inscription.employe?.prenom}{" "}
                              {inscription.employe?.nom}
                            </p>
                            {inscription.date_completion && (
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                Terminé le{" "}
                                {new Date(
                                  inscription.date_completion
                                ).toLocaleDateString("fr-FR")}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {inscription.certificat_obtenu && (
                            <Badge
                              variant="outline"
                              className="text-green-600 border-green-600"
                            >
                              <Trophy className="h-3 w-3 mr-1" />
                              Certifié
                            </Badge>
                          )}
                          {inscription.note_evaluation && (
                            <Badge variant="outline">
                              Note: {inscription.note_evaluation}/20
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={onClose}>
          Fermer
        </Button>
      </div>
    </div>
  );
}
